"use client";

import React, { useContext, useState } from "react";
import { Divider, Dropdown } from "antd";
import {
  ChevronDown,
  Power,
  Sun,
  Moon,
  User,
  Globe,
  LifeBuoy,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Button from "@/designs/Button";
import { CMSModal } from "@/context";
import { client } from "@/client/apolloClient";
import { twMerge } from "tailwind-merge";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

interface ProfileDropdownProps {
  user: any;
  setPasswordModalOpen: (open: boolean) => void;
}

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "he", label: "עברית", flag: "🇮🇱" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
];

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  user,
  setPasswordModalOpen,
}) => {
  const { theme, toggleTheme } = useContext(CMSModal);
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const handleLogout = async () => {
    localStorage.removeItem("token");
    await client.clearStore();
    router.push("/login");
  };

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
  };

  const content = (
    <div
      className={twMerge(
        "w-72 shadow-2xl rounded px-4 py-6 border transition-colors",
        theme === "dark"
          ? "bg-[#262727] border-gray-800"
          : "bg-white border-gray-200",
      )}
    >
      {/* Profile Header */}
      <div className="flex items-center space-x-4 mb-3">
        {user.img ? (
          <img src={user.img} alt="User" className="w-14 h-14 rounded-full" />
        ) : (
          <span className="w-10 h-10 rounded-full flex items-center justify-center border">
            <User />
          </span>
        )}

        <div>
          <p
            className={twMerge(
              "font-semibold text-sm",
              theme === "dark" ? "text-gray-300" : "text-gray-500",
            )}
          >
            {`${user.firstName} ${user.lastName}`}
          </p>

          <h4
            className={twMerge(
              "text-sm",
              theme === "dark" ? "text-gray-400" : "text-gray-500",
            )}
          >
            {user?.company?.name}
          </h4>

          <Link
            href="/employees/directory?viewProfile=true"
            className="text-sm !text-[#099ed1] hover:!text-[#25a9d7]"
            onClick={() => setOpen(false)}
          >
            {t("profile.viewProfile")}
          </Link>
        </div>
      </div>

      <Divider />

      {/* Security */}
      <div className="text-xs text-gray-400 font-semibold mb-1">
        {t("profile.security")}
      </div>
      <button
        onClick={() => setPasswordModalOpen(true)}
        className="text-[#099ed1] hover:text-[#25a9d7] text-sm"
      >
        {t("profile.password")}
      </button>

      <Divider />

      {/* Theme Toggle */}
      <div className="text-xs text-gray-400 font-semibold mb-1 uppercase">
        {t("profile.appearance")}
      </div>

      <button
        onClick={toggleTheme}
        className={twMerge(
          "flex items-center justify-between w-full p-2 rounded text-sm",
          theme === "dark"
            ? "hover:bg-gray-800 text-gray-300"
            : "hover:bg-gray-100 text-gray-600",
        )}
      >
        <div className="flex items-center gap-2">
          {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
          <span>
            {theme === "light" ? t("profile.darkMode") : t("profile.lightMode")}
          </span>
        </div>

        <div
          className={`w-8 h-4 rounded-full relative transition-colors ${
            theme === "dark" ? "bg-[#099ed1]" : "bg-gray-300"
          }`}
        >
          <div
            className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${
              theme === "dark" ? "right-0.5" : "left-0.5"
            }`}
          />
        </div>
      </button>

      <Divider />

      {/* Language Selector */}
      <button
        onClick={() => setLangOpen((v) => !v)}
        className={twMerge(
          "w-full flex items-center justify-between text-xs font-semibold mb-1 uppercase",
          theme === "dark" ? "text-gray-400" : "text-gray-400",
        )}
      >
        <span className="flex items-center gap-1">
          <Globe size={12} />
          {t("profile.language")}
        </span>
        <motion.span
          animate={{ rotate: langOpen ? 180 : 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
        >
          <ChevronDown size={12} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {langOpen && (
          <motion.div
            key="lang-grid"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="grid grid-cols-2 gap-1 pt-1">
              {LANGUAGES.map((lang) => {
                const isActive = i18n.language === lang.code;
                return (
                  <motion.button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className={twMerge(
                      "flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors",
                      isActive
                        ? "bg-[#099ed1] text-white font-medium"
                        : theme === "dark"
                          ? "hover:bg-gray-800 text-gray-300"
                          : "hover:bg-gray-100 text-gray-600",
                    )}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span className="truncate">{lang.label}</span>
                    {isActive && (
                      <span className="ml-auto text-white text-xs">✓</span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Divider />

      {/* Help & Support */}
      <div className="text-xs text-gray-400 font-semibold mb-1 uppercase">
        {t("profile.help")}
      </div>
      <Link
        href="/support"
        className={twMerge(
          "flex items-center gap-2 p-2 rounded text-sm mb-2 transition-colors !text-[#099ed1] hover:!text-[#25a9d7]",
       
        )}
      >
        <LifeBuoy size={16} />
        <span>{t("profile.support")}</span>
      </Link>

      <Divider />

      <Button
        onClick={handleLogout}
        className="w-full flex items-center justify-center gap-2"
        variant="outline"
      >
        <Power size={16} />
        {t("profile.logout")}
      </Button>
    </div>
  );

  return (
    <Dropdown
      trigger={["click"]}
      open={open}
      onOpenChange={setOpen}
      popupRender={() => content}
      placement="bottomRight"
    >
      <button className="flex items-center gap-2 focus:outline-none">
        {user.img ? (
          <img src={user.img} alt="User" className="w-10 h-10 rounded-full" />
        ) : (
          <span className="w-10 h-10 rounded-full flex items-center justify-center border">
            <User />
          </span>
        )}

        <motion.div
          animate={{
            rotate: open ? 180 : 0,
            scale: open ? 1.1 : 1,
          }}
          transition={{
            duration: 0.25,
            ease: "easeInOut",
          }}
        >
          <ChevronDown className="w-4 h-4" />
        </motion.div>
      </button>
    </Dropdown>
  );
};

export default ProfileDropdown;
